import React from "react";

import { FormattedHTMLMessage } from "react-intl";
import { AnnouncingHeader } from "@/components/AnnouncingHeader/AnnouncingHeader";
import { IDCheckLoopResponse, VerificationService } from "../../../../lib/types/types";
import { VerificationStepsEnum } from "../../../../lib/types/runtimeTypes";
import { uploadFiles } from "../utils/uploadFiles";
import { getRefByFieldId } from "../../../../lib/refs/refs";
import { logger } from "../../../../lib/utils/logger/logger";
import { DisplayThumbnail } from "./DisplayThumbnail";
import { IMAGE_SIDE, UploadImage } from "./UploadImage";
import { LoadingScreenComponent } from "../../../LoadingScreen/LoadingScreenComponent";

export interface IdCheckLoopRegulaProps {
  verificationService: VerificationService;
}

export interface FileToUpload {
  file: File;
  url: string;
}

const IdCheckLoopRegula = ({ verificationService }: IdCheckLoopRegulaProps) => {
  const verificationResponse = verificationService.verificationResponse as IDCheckLoopResponse;
  const { uploadUrl, errorIds } = verificationResponse;

  const [frontImage, setFrontImage] = React.useState<FileToUpload>();
  const [backImage, setBackImage] = React.useState<FileToUpload>();
  const [submitInitiated, setSubmitInitiated] = React.useState<boolean>(false);

  const handleSubmit = () => {
    setSubmitInitiated(!submitInitiated);
    uploadFiles(uploadUrl, frontImage.file, backImage.file)
      .then((response: Response) => {
        if (response.ok) {
          verificationService.submitStep(
            VerificationStepsEnum.idCheckLoop,
            verificationService.viewModel,
            verificationService.verificationResponse,
          );
        }
      })
      .catch((e) => {
        logger.error(`IdCheckLoop step failed to upload files to: ${uploadUrl}. ${e}`);
      });
  };

  const handleAddFileOnKeyUp = (e: React.KeyboardEvent<HTMLDivElement>, fieldId: string) => {
    const addFileRef = getRefByFieldId(fieldId);

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      addFileRef.click();
    }
  };
  const handleRemoveFileOnKeyUp = (
    e: React.KeyboardEvent<HTMLDivElement>,
    setImage: React.Dispatch<React.SetStateAction<FileToUpload>>,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      setImage(undefined);
    }
  };

  const handleFrontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];
    setFrontImage({ file, url: URL.createObjectURL(file) });
  };
  const handleBackUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];
    setBackImage({ file, url: URL.createObjectURL(file) });
  };

  if (submitInitiated) {
    return <LoadingScreenComponent step="idCheckLoop" />;
  }

  return (
    <div className="sid-step-id-check-loop__upload-wrapper">
      <AnnouncingHeader align="center">
        <FormattedHTMLMessage
          id="step.idCheckLoop.regula.uploadTitle"
          defaultMessage="Upload government issued ID"
        />
      </AnnouncingHeader>
      <div className="sid-header__subtitle sid-l-horz-center sid-l-space-btm-md">
        <FormattedHTMLMessage
          id="step.idCheckLoop.regula.uploadSubtitle"
          defaultMessage="Make sure information on the document can be easily read. Avoid glares."
        />
      </div>

      {errorIds && errorIds.length > 0 && (
        <div className="sid-step-id-check-loop__error-container">
          <FormattedHTMLMessage
            id="step.idCheckLoop.regula.uploadFileError"
            defaultMessage="There was an error uploading your files. Please try again."
          />
        </div>
      )}

      <div className="sid-step-id-check-loop__upload-container">
        {frontImage ? (
          <DisplayThumbnail
            fileToUpload={frontImage}
            onRemove={handleRemoveFileOnKeyUp}
            setImage={setFrontImage}
          />
        ) : (
          <UploadImage
            addFile={handleAddFileOnKeyUp}
            upload={handleFrontUpload}
            imageSide={IMAGE_SIDE.FRONT}
          />
        )}

        {backImage ? (
          <DisplayThumbnail
            fileToUpload={backImage}
            onRemove={handleRemoveFileOnKeyUp}
            setImage={setBackImage}
          />
        ) : (
          <UploadImage
            addFile={handleAddFileOnKeyUp}
            upload={handleBackUpload}
            imageSide={IMAGE_SIDE.BACK}
          />
        )}
      </div>

      <div className="sid-submit sid-l-horz-center sid-l-space-top-md sid-l-space-btm-md">
        <button
          onClick={handleSubmit}
          type="button"
          className="sid-btn sid-btn--dark"
          data-testId="id-check-submit-button"
          aria-label="submit"
          disabled={!frontImage || !backImage}
        >
          <FormattedHTMLMessage id="step.idCheckLoop.regula.submit" defaultMessage="Submit" />
        </button>
      </div>
    </div>
  );
};

export const IdCheckLoopRegulaComponent = IdCheckLoopRegula;
